// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import {
  ModalCustomCancel,
  ModalCustomDeleteSolid,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Services
import { getExamsForProgressiveLenses } from "src/services/clinics/api";

// Types
import { ProgressiveLensType } from "src/services/clinics/types";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

type DeleteProgressiveModalType = {
  open: boolean;
  selectedLens: ProgressiveLensType | undefined;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteProgressiveModal = ({
  open,
  selectedLens,
  onClose,
  onConfirm,
}: DeleteProgressiveModalType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { getToken } = useTokenAccess();

  const [linkedExamCount, setLinkedExamCount] = useState(0);

  // refetch the related exams every time the clinic or selected lens change
  const fetchLinkedExams = useCallback(async () => {
    if (!selectedLens) return;

    const token = await getToken();
    getExamsForProgressiveLenses(token, clinicInfo?.id, selectedLens?.id).then(
      (res) => {
        setLinkedExamCount(res?.length);
      }
    );
  }, [clinicInfo?.id, selectedLens, getToken]);

  useEffect(() => {
    if (open && selectedLens) {
      fetchLinkedExams();
    }
  }, [fetchLinkedExams, open, selectedLens]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Delete Lens Option?")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button sx={ModalCustomDeleteSolid} onClick={onConfirm}>
            {t("Delete")}
          </Button>
        </Grid>
      }
    >
      <Grid p="16px" display="flex" flexDirection="column" gap="20px">
        {linkedExamCount > 0 && (
          <Alert
            variant="warning"
            alertContent={t(
              "This lens is linked to one or more exams that haven't been started. Deleting it will also delete the exams."
            )}
          />
        )}
        <Typography fontSize="14px">
          {t("Are you sure you want to delete '{{ lensOptionName }}'?", {
            lensOptionName: selectedLens?.name ?? "",
          })}
        </Typography>
      </Grid>
    </DefaultModal>
  );
};

export default DeleteProgressiveModal;
